import React from 'react';
import { Nav, Footer, DisclaimerBar } from '../components/Chrome';
import { ReportBody } from './ReportViewer';

const SAMPLE = {
  address: '168 Fiesta Way, Fort Lauderdale, FL 33301',
  folio: '5042-05-04-0180',
  asking_price: 680000,
  intended_use: 'buy_hold',
  lang: 'en',
  created_at: '2026-01-14T00:00:00Z',
  report: {
    verdict: 'negotiate',
    verdict_confidence: 74,
    verdict_headline: 'Model-assisted evidence supports moving forward only with structural price and insurance concessions.',
    executive_summary: 'The subject sits inside a stable but flood-exposed Fort Lauderdale corridor. Model-assisted AVM bands indicate the asking price is at the upper edge of defensible range, while insurance economics and title-chain gaps materially compress buy-and-hold yield. A verdict of NEGOTIATE is warranted, anchored at AVM low plus documented insurance delta. Do not proceed without USPAP-grade appraisal and title cure.',
    avm_range: { low: 612000, median: 648000, high: 684000, confidence: 68, method_note: 'Model-assisted range from location typology, size class, and ZIP median trajectory. Not an appraisal.' },
    comps_analysis: {
      summary: 'Within 1-mile radius, waterfront and non-waterfront trade divergence is severe. Model-assisted evidence supports negotiating below list; certified comps must be pulled from MLS at contract stage.',
      verification_required: [
        'Sold comps last 180 days, same submarket',
        'Waterfront vs non-waterfront segregation',
        'Days on market distribution',
        'Original list vs closed spread',
      ],
    },
    risk_stack: [
      { category: 'flood', level: 'moderate', description: 'Address geometry likely within FEMA AE band. Base flood elevation impacts insurance and financing.', verification: 'Pull official FIRM panel + elevation certificate.', confidence: 62 },
      { category: 'insurance', level: 'high', description: 'Florida coastal premiums have re-priced sharply post-2022. Estimated annual premium $8.4k–$14.2k materially compresses yield.', verification: 'Obtain 3 binding quotes prior to inspection contingency expiry.', confidence: 70 },
      { category: 'permits', level: 'low', description: 'ZIP-level permit activity above trailing 12-month median; suggests active neighborhood investment.', verification: 'Pull building department history for the subject folio.', confidence: 65 },
      { category: 'title', level: 'moderate', description: 'Public record chain shows a gap between 1998–2004 that should be cured.', verification: 'Order full title search + owner affidavit.', confidence: 55 },
      { category: 'condition', level: 'unknown', description: 'No inspection data available in intake.', verification: 'Four-point + wind mitigation inspection prior to close.', confidence: 0 },
    ],
    source_ledger: [
      { field: 'Asking price', value: '$680,000', source: 'user_input', confidence: 100, missingness: 'none' },
      { field: 'Address geometry', value: '168 Fiesta Way, 33301', source: 'user_input', confidence: 100, missingness: 'none' },
      { field: 'Year built', value: '1962 (typical vintage)', source: 'model_hypothesis', confidence: 55, missingness: 'partial' },
      { field: 'Flood zone', value: 'AE band (verify)', source: 'model_hypothesis', confidence: 60, missingness: 'partial' },
      { field: 'Sold comparables', value: 'range only', source: 'model_hypothesis', confidence: 50, missingness: 'partial' },
      { field: 'Owner entity', value: 'unknown', source: 'not_available', confidence: 0, missingness: 'full' },
      { field: 'Title chain 1998–2004', value: 'gap flagged', source: 'model_hypothesis', confidence: 40, missingness: 'partial' },
      { field: 'Insurance premium range', value: '$8,400–$14,200', source: 'model_hypothesis', confidence: 50, missingness: 'partial' },
    ],
    red_flags: [
      'Projected annual insurance premium exceeds 12% of gross annual rent at market rate.',
      'Title chain contains an unaddressed period between 1998 and 2004.',
      'Days on market appear to exceed ZIP median — signal of overpricing or condition friction.',
    ],
    offer_checklist: [
      'Anchor initial offer at AVM low ($612k) with insurance escrow adjustment.',
      'Require seller-paid four-point + wind mitigation inspection credit.',
      'Insert 21-day inspection contingency and 30-day financing contingency.',
      'Require full title commitment and owner affidavit before earnest release.',
    ],
    verification_questions: [
      'What is the FIRM panel effective date and BFE for this parcel?',
      'What is the actual annual premium quoted by 3 independent carriers?',
      'What sold comparables exist within 0.5 miles in the trailing 180 days?',
      'Is the owner an LLC, individual, or estate?',
    ],
    negotiation_levers: [
      'Insurance premium delta above ZIP median',
      'Title cure cost estimate',
      'Days-on-market vs ZIP median (overpricing signal)',
      'Deferred maintenance suggested by vintage',
    ],
    disclaimer: 'Not an appraisal. Not investment advice. Model-assisted due diligence.',
  },
};

export default function SampleReport() {
  return (
    <div>
      <Nav />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-10">
        <div className="border hairline bg-amber-500/10 border-amber-500/40 p-3 mono text-[10px] uppercase tracking-widest2 text-amber-300 mb-6">
          ⚠ SAMPLE / SPECIMEN — Fictional property. Illustrative of Pythia output only.
        </div>
        <ReportBody data={SAMPLE} />
      </div>
      <DisclaimerBar />
      <Footer />
    </div>
  );
}
